/**
 * Resume Builder — "Make Resume" from the Case Dashboard.
 *
 * Builds a Canadian-format (reverse-chronological) resume for the client of this case:
 *   - opens with the saved resume, or a free prefill from the case (analysis + lead);
 *   - "Draft from case (AI)" fills every section from the analysis, the conversation and
 *     the client's uploaded files, and lists what is still missing (gaps);
 *   - the adviser adds / edits / reorders / removes anything (ResumeSectionEditors);
 *   - live PDF preview with the adviser's branding (ResumeTemplateSettings);
 *   - Save, Download PDF, Save to case files (replaces the previous copy), Delete.
 *
 * Tabs built on the saved resume:
 *   - Job applications: paste a posting -> fit, tailored resume, cover letter, interview prep
 *   - French / LinkedIn / growth suggestions
 *   - Client follow-ups: "ask the client" for missing info (+ resume review), with answers
 * and "Email…" sends any of the PDFs to the client or an employer.
 *
 * Backend: /resumes/case/:threadId* on maven-platform-server. ClientResume is the single
 * source of truth; the PDF is always rendered from it (or from the unsaved editor state
 * for the preview).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useAuthenticatedAxios from '../../../../auth/AuthenticatedAxios';
import { useToast } from '../../../../Context/ToastContext';
import { useConfirm } from '../../../../Context/ConfirmContext';
import {
  Overlay, Shell, TopBar, Title, Status, Spacer, Body, EditorPane, PreviewPane, Iframe, PreviewEmpty,
  Row, Field, Label, Hint, Btn, IconBtn, Banner, Check, Select,
} from './resumeStyles';
import {
  Section, ContactEditor, SummaryEditor, SkillsEditor, ExperienceEditor, EducationEditor,
  CertificationsEditor, LanguagesEditor, AdditionalEditor,
} from './ResumeSectionEditors';
import ResumeTemplateSettings from './ResumeTemplateSettings';
import JobApplications from './JobApplications';
import ResumeExtras from './ResumeExtras';
import EmailDocumentsDialog from './EmailDocumentsDialog';
import { InfoRequestComposer, InfoRequestsPanel } from './InfoRequests';
import styled from 'styled-components';

const TabStrip = styled.div`
  display: flex; gap: 4px; padding: 0 18px; border-bottom: 1px solid rgba(120,140,180,0.15); overflow-x: auto;
`;
const TabBtn = styled.button`
  padding: 11px 14px; background: none; border: none; cursor: pointer; white-space: nowrap; font-size: 13.5px; font-weight: 600;
  color: ${p => (p.$active ? '#fff' : '#8b93a7')};
  border-bottom: 2px solid ${p => (p.$active ? '#8b5cf6' : 'transparent')};
  .badge { margin-left: 6px; font-size: 11px; padding: 1px 7px; border-radius: 999px; background: rgba(34,197,94,0.2); color: #86efac; }
`;
const FullPane = styled.div`flex: 1; min-height: 0; overflow-y: auto; padding: 18px;`;

const LIST_KEYS = ['skills', 'experience', 'education', 'certifications', 'languages', 'volunteer', 'additional'];

/** Server content omits empty keys — give the editor a complete shape. */
const withDefaults = (c = {}) => {
  const out = { contact: { ...(c.contact || {}) }, summary: c.summary || '' };
  for (const k of LIST_KEYS) out[k] = Array.isArray(c[k]) ? c[k] : [];
  return out;
};

const hasAnyContent = (c) => !!(
  c.summary || Object.values(c.contact || {}).some(Boolean) || LIST_KEYS.some(k => (c[k] || []).length)
);

const base64ToBlobUrl = (b64) => {
  const bytes = Uint8Array.from(atob(b64), ch => ch.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
};

const errMsg = (e, fallback) => e?.response?.data?.message || e?.response?.data?.error || fallback;

export default function ResumeBuilder({ threadId, clientName, onClose }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');               // '', 'save', 'draft', 'file', 'download', 'delete'
  const [resume, setResume] = useState(null);         // saved server DTO (null = none yet)
  const [content, setContent] = useState(withDefaults());
  const [showContact, setShowContact] = useState(null); // null = template default
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [sources, setSources] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [pendingAi, setPendingAi] = useState(null);   // { sources, draftedAt } until saved
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [autoPreview, setAutoPreview] = useState(true);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [gapsOpen, setGapsOpen] = useState(true);
  const editorRef = useRef(null);
  const [tab, setTab] = useState('resume');
  const [composer, setComposer] = useState(null);     // { gaps, source, sourceRefId, review } | null
  const [emailPreset, setEmailPreset] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [followUpsKey, setFollowUpsKey] = useState(0);

  const snapshot = useMemo(() => JSON.stringify({ content, showContact, gaps }), [content, showContact, gaps]);
  const dirty = savedSnapshot === null ? hasAnyContent(content) : snapshot !== savedSnapshot;

  // ---- load ----------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/resumes/case/${threadId}`);
      const d = r.data.data;
      setSources(d.sources);
      if (d.resume) {
        const c = withDefaults(d.resume.content);
        setResume(d.resume);
        setContent(c);
        setShowContact(d.resume.showCandidateContact);
        setGaps(d.resume.aiDraft?.gaps || []);
        setSavedSnapshot(JSON.stringify({ content: c, showContact: d.resume.showCandidateContact, gaps: d.resume.aiDraft?.gaps || [] }));
      } else {
        setResume(null);
        setContent(withDefaults(d.prefill));
        setShowContact(null);
        setGaps([]);
        setSavedSnapshot(null);
      }
      setPendingAi(null);
    } catch (e) {
      showToast(errMsg(e, 'Could not load the resume'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  const loadFollowUps = useCallback(async () => {
    try { const r = await api.get(`/info-requests/case/${threadId}`); setFollowUps(r.data.data.requests || []); }
    catch { setFollowUps([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);
  useEffect(() => { loadFollowUps(); }, [loadFollowUps, followUpsKey]);

  /** Refresh only the saved resume's metadata (French / LinkedIn / growth) — never the editor. */
  const refreshResumeMeta = useCallback(async () => {
    try { const r = await api.get(`/resumes/case/${threadId}`); if (r.data.data.resume) setResume(r.data.data.resume); }
    catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // ---- preview (unsaved editor state, rendered by the server) --------------
  const previewSeq = useRef(0);
  const refreshPreview = useCallback(async () => {
    const seq = ++previewSeq.current;
    setPreviewing(true);
    try {
      const r = await api.post(`/resumes/case/${threadId}/preview`, { content, showCandidateContact: showContact });
      if (seq !== previewSeq.current) return;          // a newer render superseded this one
      const url = base64ToBlobUrl(r.data.data.pdfBase64);
      setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      if (seq === previewSeq.current) showToast(errMsg(e, 'Preview failed'), 'error');
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, content, showContact]);

  useEffect(() => {
    if (loading || !autoPreview) return undefined;
    const t = setTimeout(refreshPreview, 1200);
    return () => clearTimeout(t);
  }, [loading, autoPreview, refreshPreview]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // ---- actions -------------------------------------------------------------
  const save = useCallback(async ({ quiet = false } = {}) => {
    setBusy('save');
    try {
      const body = { content, showCandidateContact: showContact };
      // Persist the AI gap list (incl. a dismissed = empty list) once a draft exists.
      const ai = pendingAi || resume?.aiDraft;
      if (ai) Object.assign(body, { aiGaps: gaps, aiSources: ai.sources || [], aiDraftedAt: ai.draftedAt });
      const r = await api.put(`/resumes/case/${threadId}`, body);
      setResume(r.data.data.resume);
      setSavedSnapshot(JSON.stringify({ content, showContact, gaps }));   // keep local (incl. empty rows being filled)
      setPendingAi(null);
      if (!quiet) showToast('Resume saved', 'success');
      return true;
    } catch (e) {
      showToast(errMsg(e, 'Failed to save the resume'), 'error');
      return false;
    } finally { setBusy(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, content, showContact, pendingAi, gaps, resume]);

  // Ctrl/Cmd+S saves
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (!busy) save(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, busy]);

  const draftWithAi = async () => {
    const hasData = hasAnyContent(content);
    if (hasData && !(await confirm({
      title: 'Draft from the case with AI',
      message: 'The AI reads the case analysis, the conversation and the client\'s uploaded files, keeps the facts already in the editor, and fills in the rest. The editor content is replaced with the draft — nothing is saved until you click Save. Uses AI tokens.',
      confirmText: 'Draft resume', type: 'info',
    }))) return;
    setBusy('draft');
    try {
      const r = await api.post(`/resumes/case/${threadId}/draft`, { current: hasData ? content : undefined });
      const d = r.data.data;
      setContent(withDefaults(d.content));
      setGaps(d.gaps || []);
      setPendingAi({ sources: d.sources || [], draftedAt: d.draftedAt });
      setGapsOpen(true);
      // The gap list renders at the top of the editor — bring it into view.
      editorRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      const n = (d.gaps || []).length;
      showToast(n
        ? `Draft ready — ${n} item${n > 1 ? 's' : ''} to confirm with the client (listed at the top). Review, then Save.`
        : 'Draft ready — review it, then Save', 'success');
    } catch (e) {
      // 402 (out of tokens) is handled globally by the axios interceptor (opens the purchase screen).
      if (e?.response?.status !== 402) showToast(errMsg(e, 'AI draft failed'), 'error');
    } finally { setBusy(''); }
  };

  const ensureSaved = async () => {
    if (!content.contact?.fullName?.trim()) { showToast('Add the client\'s full name first', 'warning'); return false; }
    if (!dirty && resume) return true;
    return save({ quiet: true });
  };

  const download = async () => {
    if (!(await ensureSaved())) return;
    setBusy('download');
    try {
      const r = await api.get(`/resumes/case/${threadId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Resume - ${content.contact.fullName.trim()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { showToast('Download failed', 'error'); }
    finally { setBusy(''); }
  };

  const saveToFiles = async () => {
    if (!(await ensureSaved())) return;
    setBusy('file');
    try {
      const r = await api.post(`/resumes/case/${threadId}/save-to-files`);
      const { savedFile, replaced } = r.data.data;
      setResume(prev => ({ ...(prev || {}), savedFile }));
      showToast(`${replaced ? 'Updated' : 'Saved'} “${savedFile.originalName}” in the case files`, 'success');
    } catch (e) { showToast(errMsg(e, 'Could not save to case files'), 'error'); }
    finally { setBusy(''); }
  };

  const remove = async () => {
    if (!resume) return;
    if (!(await confirm({
      title: 'Delete this resume?',
      message: `The resume content for this case is deleted.${resume.savedFile ? ' The PDF copy already saved in the case files is kept — remove it from the Files panel if needed.' : ''}`,
      confirmText: 'Delete', type: 'danger',
    }))) return;
    setBusy('delete');
    try {
      await api.delete(`/resumes/case/${threadId}`);
      showToast('Resume deleted', 'success');
      await load();
    } catch (e) { showToast(errMsg(e, 'Failed to delete'), 'error'); }
    finally { setBusy(''); }
  };

  const close = async () => {
    if (dirty && !(await confirm({
      title: 'Discard unsaved changes?', message: 'You have changes to this resume that are not saved.',
      confirmText: 'Discard', cancelText: 'Keep editing', type: 'warning',
    }))) return;
    onClose();
  };

  const set = (key) => (value) => setContent(c => ({ ...c, [key]: value }));
  const displayName = content.contact?.fullName || clientName || 'Client';
  const answered = followUps.filter(r => r.status === 'answered');
  // Answers that arrived after the resume was last saved — worth folding in.
  const freshAnswers = answered.filter(r => !resume?.updatedAt || new Date(r.answeredAt) > new Date(resume.updatedAt));
  const reviewAsk = freshAnswers.find(r => r.review?.decision === 'changes_requested');
  const openComposer = (opts) => setComposer({ gaps: [], source: 'manual', ...opts });
  const sourceLine = sources && [
    sources.hasAnalysis ? 'case analysis' : null,
    sources.messageCount ? `${sources.messageCount} messages` : null,
    sources.parsedFileCount ? `${sources.parsedFileCount} parsed file${sources.parsedFileCount > 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(' · ');

  return createPortal(
    <Overlay onMouseDown={e => e.target === e.currentTarget && close()}>
      <Shell>
        <TopBar>
          <Title>
            Resume — {displayName}
            {!loading && <Status $dirty={dirty}>{dirty ? (resume ? 'Unsaved changes' : 'Not saved yet') : 'Saved'}</Status>}
          </Title>
          <Spacer />
          {tab === 'resume' && gaps.length > 0 && (
            <Btn $sm $variant="ghost" style={{ color: '#fcd34d', borderColor: 'rgba(245,158,11,0.45)' }}
              onClick={() => { setGapsOpen(true); editorRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }}>
              ⚠ {gaps.length} to ask the client
            </Btn>
          )}
          {tab === 'resume' && <>
            <Btn $variant="ai" onClick={draftWithAi} disabled={loading || !!busy} title={sourceLine ? `Uses: ${sourceLine}` : undefined}>
              {busy === 'draft' ? 'Drafting…' : '✨ Draft from case (AI)'}
            </Btn>
            <Btn onClick={() => save()} disabled={loading || !!busy || !dirty}>{busy === 'save' ? 'Saving…' : 'Save'}</Btn>
            <Btn $variant="ghost" onClick={download} disabled={loading || !!busy}>{busy === 'download' ? 'Preparing…' : 'Download PDF'}</Btn>
            <Btn $variant="green" onClick={saveToFiles} disabled={loading || !!busy}
              title={resume?.savedFile ? `Replaces “${resume.savedFile.originalName}” saved ${new Date(resume.savedFile.savedAt).toLocaleString()}` : 'Store the PDF in this case\'s files'}>
              {busy === 'file' ? 'Saving…' : resume?.savedFile ? 'Update in case files' : 'Save to case files'}
            </Btn>
            <Btn $variant="ghost" disabled={loading || !resume || dirty} title={dirty ? 'Save first' : 'The client approves or asks for changes from a link'}
              onClick={() => openComposer({ review: true, source: 'resume_gaps' })}>Client review</Btn>
          </>}
          <Btn $variant="ghost" disabled={loading || !resume}
            title={!resume ? 'Save the resume first' : dirty ? 'Unsaved changes are not included — Save first' : 'Email PDFs to the client or an employer'}
            onClick={() => setEmailPreset({})}>✉ Email…</Btn>
          <Btn $variant="ghost" onClick={() => setTemplateOpen(true)} disabled={loading}>Resume template</Btn>
          {tab === 'resume' && resume && <IconBtn $danger title="Delete resume" onClick={remove} disabled={!!busy}>🗑</IconBtn>}
          <IconBtn title="Close" onClick={close}>×</IconBtn>
        </TopBar>

        <TabStrip>
          {[['resume', 'Resume'], ['jobs', 'Job applications'], ['extras', 'French · LinkedIn · Growth'], ['followups', 'Client follow-ups']].map(([k, l]) => (
            <TabBtn key={k} $active={tab === k} onClick={() => setTab(k)}>
              {l}
              {k === 'followups' && freshAnswers.length > 0 && <span className="badge">{freshAnswers.length} new</span>}
            </TabBtn>
          ))}
        </TabStrip>

        {tab === 'jobs' && (
          <div style={{ flex: 1, minHeight: 0 }}>
            <JobApplications threadId={threadId} hasSavedResume={!!resume} clientName={displayName}
              onAskClient={(qs, jobId) => openComposer({ gaps: qs, source: 'job_fit', sourceRefId: jobId })}
              onEmail={(preset) => setEmailPreset(preset)} />
          </div>
        )}
        {tab === 'extras' && (
          <FullPane><ResumeExtras threadId={threadId} resume={resume} resumeUpdatedAt={resume?.updatedAt} onChanged={refreshResumeMeta} /></FullPane>
        )}
        {tab === 'followups' && (
          <FullPane>
            <InfoRequestsPanel threadId={threadId} refreshKey={followUpsKey} onAsk={() => openComposer({})} />
          </FullPane>
        )}

        {tab === 'resume' && <Body>
          <EditorPane ref={editorRef}>
            {loading ? <Hint>Loading…</Hint> : (
              <>
                {!resume && !pendingAi && (
                  <Banner>
                    Started from what the case already knows{sourceLine ? ` (${sourceLine})` : ''}. Click
                    <b> ✨ Draft from case</b> to have the AI fill every section from the conversation and the client's files,
                    or fill it in yourself below.
                  </Banner>
                )}
                {freshAnswers.length > 0 && (
                  <Banner>
                    {reviewAsk
                      ? <>✏️ The client asked for changes: <b>{reviewAsk.review.comment}</b></>
                      : <>💬 The client answered {freshAnswers.length} follow-up{freshAnswers.length > 1 ? 's' : ''} since this resume was saved.</>}
                    <Row $mt>
                      <Btn $sm $variant="ai" onClick={draftWithAi} disabled={!!busy}>✨ Update the resume with their answers</Btn>
                      <Btn $sm $variant="ghost" onClick={() => setTab('followups')}>See answers</Btn>
                    </Row>
                  </Banner>
                )}
                {gaps.length > 0 && (
                  // Pinned to the top of the editor so it stays visible while the adviser scrolls and fills it in.
                  <div style={{ position: 'sticky', top: -18, zIndex: 2, margin: '-18px -18px 14px', padding: '18px 18px 0', background: '#10151e' }}>
                    <Banner $tone="warn" style={{ background: '#2a2416', marginBottom: 12 }}>
                      <Row style={{ alignItems: 'center', flexWrap: 'nowrap' }}>
                        <b style={{ flex: 1 }}>Still missing — ask the client ({gaps.length})</b>
                        <Btn $sm onClick={() => openComposer({ gaps, source: 'resume_gaps' })}>Ask the client</Btn>
                        <Btn $sm $variant="ghost" onClick={() => setGapsOpen(o => !o)}>{gapsOpen ? 'Collapse' : 'Show'}</Btn>
                        <Btn $sm $variant="ghost" onClick={() => setGaps([])} title="Clear the list (saved with the resume)">Dismiss</Btn>
                      </Row>
                      {gapsOpen && <ul style={{ maxHeight: 220, overflowY: 'auto' }}>{gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>}
                    </Banner>
                  </div>
                )}
                <Hint $mb>
                  Canadian format: most recent first, 1–2 pages, no photo, date of birth, age, marital status, nationality or SIN.{' '}
                  <button type="button" onClick={() => setShowTips(s => !s)} style={{ background: 'none', border: 'none', color: '#a5b4fc', cursor: 'pointer', padding: 0, fontSize: 12 }}>
                    {showTips ? 'Hide tips' : 'More tips'}
                  </button>
                </Hint>
                {showTips && (
                  <Banner>
                    <ul>
                      <li>Bullets start with an action verb and show results where known (numbers, scale, safety record).</li>
                      <li>Foreign credentials: keep the original name and add the Canadian equivalency (e.g. WES ECA) when the client has one.</li>
                      <li>Trades: list the Certificate of Qualification / licence number and tickets (WHMIS, Working at Heights, forklift).</li>
                      <li>Work authorization is optional — include it only if the client agrees; it often helps newcomers.</li>
                    </ul>
                  </Banner>
                )}

                <Section title="Contact & headline">
                  <ContactEditor contact={content.contact} onChange={set('contact')} />
                  <Row $mt style={{ alignItems: 'center' }}>
                    <Field $min="260px">
                      <Label>Candidate contact on the PDF</Label>
                      <Select value={showContact === null ? 'default' : showContact ? 'show' : 'hide'}
                        onChange={e => setShowContact(e.target.value === 'default' ? null : e.target.value === 'show')}>
                        <option value="default">Use template default</option>
                        <option value="show">Show the candidate's phone / email</option>
                        <option value="hide">Hide — employers contact my company</option>
                      </Select>
                    </Field>
                  </Row>
                </Section>
                <Section title="Professional summary"><SummaryEditor value={content.summary} onChange={set('summary')} /></Section>
                <Section title="Skills" count={content.skills.reduce((n, g) => n + (g.items?.length || 0), 0)}>
                  <SkillsEditor groups={content.skills} onChange={set('skills')} />
                </Section>
                <Section title="Professional experience" count={content.experience.length}
                  hint="Most recent first. Use ↑ ↓ to reorder.">
                  <ExperienceEditor items={content.experience} onChange={set('experience')} />
                </Section>
                <Section title="Education" count={content.education.length}>
                  <EducationEditor items={content.education} onChange={set('education')} />
                </Section>
                <Section title="Certifications & licences" count={content.certifications.length} defaultOpen={content.certifications.length > 0}>
                  <CertificationsEditor items={content.certifications} onChange={set('certifications')} />
                </Section>
                <Section title="Languages" count={content.languages.length}>
                  <LanguagesEditor items={content.languages} onChange={set('languages')} />
                </Section>
                <Section title="Volunteer experience" count={content.volunteer.length} defaultOpen={content.volunteer.length > 0}>
                  <ExperienceEditor volunteer items={content.volunteer} onChange={set('volunteer')} />
                </Section>
                <Section title="Custom sections" count={content.additional.length} defaultOpen={content.additional.length > 0}
                  hint="Anything else an employer should see: driver's licence, equipment operated, awards, publications…">
                  <AdditionalEditor items={content.additional} onChange={set('additional')} />
                </Section>
              </>
            )}
          </EditorPane>

          <PreviewPane>
            <Row style={{ alignItems: 'center' }}>
              <Label style={{ fontSize: 13, color: '#c7cee0' }}>Preview</Label>
              {previewing && <Hint>rendering…</Hint>}
              <Spacer />
              <Check><input type="checkbox" checked={autoPreview} onChange={e => setAutoPreview(e.target.checked)} />Auto-refresh</Check>
              <Btn $sm $variant="ghost" onClick={refreshPreview} disabled={loading || previewing}>Refresh</Btn>
            </Row>
            {!content.contact?.fullName?.trim() && !loading && (
              <Banner $tone="warn" style={{ marginBottom: 0 }}>Add the client's full name — it heads the resume.</Banner>
            )}
            {previewUrl ? <Iframe title="Resume preview" src={previewUrl} /> : (
              <PreviewEmpty>{loading ? 'Loading…' : 'The PDF preview appears here.'}</PreviewEmpty>
            )}
          </PreviewPane>
        </Body>}
      </Shell>

      {templateOpen && (
        <ResumeTemplateSettings onClose={() => setTemplateOpen(false)} onSaved={refreshPreview} />
      )}
      {composer && (
        <InfoRequestComposer threadId={threadId} gaps={composer.gaps} source={composer.source} sourceRefId={composer.sourceRefId}
          defaultEmail={content.contact?.email || resume?.content?.contact?.email || ''} defaultName={displayName}
          allowReview={!!resume} defaultReview={!!composer.review}
          onClose={() => setComposer(null)}
          onSent={() => { setFollowUpsKey(k => k + 1); setTab('followups'); }} />
      )}
      {emailPreset && (
        <EmailDocumentsDialog threadId={threadId} clientEmail={resume?.content?.contact?.email || content.contact?.email || ''}
          clientName={displayName} hasFrench={!!resume?.french} preset={emailPreset} onClose={() => setEmailPreset(null)} />
      )}
    </Overlay>,
    document.body
  );
}
